/**
 * Theme Editor / Website Editor — shared section & block type definitions.
 *
 * IMPORTANT (i18n preservation): the DEFAULT sections shipped with the app (see
 * backend `routers_theme.py` DEFAULT_THEME) intentionally have empty text fields.
 * `resolveField()` below falls back to either:
 *   - an existing global translation key (string value, e.g. "home.hero_title_line1"), or
 *   - an inline {fr,en,de} object (used for content that lives only on its own
 *     page today - Notre histoire / Savoir-faire / Collaborations / Contact - and
 *     was never added to the global translations.js keyspace)
 * so pages keep full multi-language coverage until an admin explicitly edits a
 * field (which then becomes plain-text/French-only, consistent with the rest of
 * the admin-authored content in this app, e.g. the announcement bar).
 *
 * Any section/block added by an admin (new id) has no fallback entry: its text is
 * whatever was typed (French-only v1).
 */

export const I18N_FALLBACKS = {
  "hero-default": {
    eyebrow: "home.hero_eyebrow",
    title_line1: "home.hero_title_line1",
    title_line2: "home.hero_title_line2",
    subtitle: "home.hero_subtitle",
    cta1_label: "home.hero_cta_boutique",
    cta2_label: "home.hero_cta_histoire",
    image_alt: "home.alt_hero",
  },
  "collections-default": { title: "home.collections_title" },
  "featured-default": { title: "home.featured_title" },
  "story-founder-default": {
    title: "home.founder_title", quote: "home.founder_quote", body_p1: "home.founder_p1",
    body_p2: "home.founder_p2", cta_label: "home.founder_cta", image_alt: "home.alt_founder",
  },
  "story-craft-default": {
    title: "home.craft_title", body_p1: "home.craft_p1", body_p2: "home.craft_p2",
    cta_label: "home.craft_cta", image_alt: "home.alt_craft",
  },
  "lookbook-default": {
    title: "home.lookbook_title", subtitle: "home.lookbook_subtitle", cta_label: "home.lookbook_cta",
    image_main_alt: "home.alt_lookbook_group", image_secondary_alt: "home.alt_lookbook_solo",
  },
  "principles-default": {
    title: "home.principles_title",
    item1_title: "home.principle1_title", item1_text: "home.principle1_text",
    item2_title: "home.principle2_title", item2_text: "home.principle2_text",
    item3_title: "home.principle3_title", item3_text: "home.principle3_text",
  },
  "newsletter-default": { title: "home.newsletter_title", subtitle: "home.newsletter_subtitle" },
  "nav-histoire": { label: "nav.histoire" },
  "nav-artisanat": { label: "nav.artisanat" },
  "nav-lookbook": { label: "nav.lookbook" },
  "nav-contact": { label: "nav.contact" },
  "nav-decouvrir": { label: "footer.decouvrir" },
  "nav-decouvrir-journal": { label: "nav.journal" },
  "nav-decouvrir-ateliers": { label: "nav.ateliers" },
  "nav-decouvrir-partenariats": { label: "nav.partenariats" },
  "footer-col-boutique": { title: "footer.boutique" },
  "footer-col-decouvrir": { title: "footer.decouvrir" },
  "footer-col-aide": { title: "footer.aide" },
  "footer-boutique-nouveautes": { label: "shop.nouveautes" },
  "footer-boutique-keffiehs": { label: "shop.keffiehs" },
  "footer-boutique-robes": { label: "shop.robes_abayas" },
  "footer-boutique-brodees": { label: "shop.pieces_brodees" },
  "footer-boutique-chales": { label: "shop.chales_ponchos" },
  "footer-boutique-all": { label: "shop.all" },
  "footer-decouvrir-histoire": { label: "nav.histoire" },
  "footer-decouvrir-artisanat": { label: "nav.artisanat" },
  "footer-decouvrir-lookbook": { label: "nav.lookbook" },
  "footer-decouvrir-journal": { label: "nav.journal" },
  "footer-decouvrir-ateliers": { label: "nav.ateliers" },
  "footer-decouvrir-partenariats": { label: "nav.partenariats" },
  "footer-decouvrir-contact": { label: "nav.contact" },
  "footer-aide-livraison": { label: "footer.livraison_retours" },
  "footer-aide-suivi": { label: "tracking.title" },
  "footer-aide-faq": { label: "footer.faq" },
  "footer-aide-cgv": { label: "footer.cgv" },
  "footer-aide-confidentialite": { label: "footer.confidentialite" },
  "footer-aide-cookies": { label: "footer.cookies" },
  "footer-aide-mentions": { label: "footer.mentions_legales" },

  // ---- Notre histoire -------------------------------------------------------
  "hist-intro": {
    label: { fr: "Notre histoire", en: "Our story", de: "Unsere Geschichte" },
    title: { fr: "Entre la Palestine, la Syrie et la France", en: "Between Palestine, Syria and France", de: "Zwischen Palästina, Syrien und Frankreich" },
    body: {
      fr: "LEVAMI est née d'un parcours personnel, de liens familiaux et d'une envie simple : donner une place actuelle à des vêtements, des textiles et des savoir-faire du Levant.",
      en: "LEVAMI was born from a personal journey, family ties and a simple wish: to give a contemporary place to garments, textiles and craftsmanship from the Levant.",
      de: "LEVAMI entstand aus einem persönlichen Weg, familiären Bindungen und einem einfachen Wunsch: Kleidungsstücken, Textilien und Handwerkskunst aus der Levante einen zeitgemäßen Platz zu geben.",
    },
  },
  "hist-s1": {
    title: { fr: "Des racines à Tibériade, une enfance en Syrie", en: "Roots in Tiberias, a childhood in Syria", de: "Wurzeln in Tiberias, eine Kindheit in Syrien" },
    body: {
      fr: "Iwan est franco-syrien et d'origine palestinienne. Sa famille vient de Tibériade, en Palestine.\n\nIl est né et a grandi en Syrie, au contact des communautés palestiniennes de Damas et de sa campagne. Les histoires d'exil, de vie quotidienne et de transmission ont façonné son regard sur les vêtements, les motifs et les objets.",
      en: "Iwan is French-Syrian, of Palestinian origin. His family comes from Tiberias, in Palestine.\n\nHe was born and raised in Syria, close to the Palestinian communities of Damascus and its countryside. Stories of exile, everyday life and heritage shaped the way he sees garments, patterns and objects.",
      de: "Iwan ist französisch-syrisch und palästinensischer Abstammung. Seine Familie stammt aus Tiberias in Palästina.\n\nEr wurde in Syrien geboren und wuchs dort auf, im Kontakt mit den palästinensischen Gemeinschaften von Damaskus und seinem Umland. Geschichten von Exil, Alltag und Weitergabe prägten seinen Blick auf Kleidung, Muster und Gegenstände.",
    },
  },
  "hist-portrait": { image_0_alt: { fr: "Portrait d'Iwan, fondateur de LEVAMI.", en: "Portrait of Iwan, founder of LEVAMI.", de: "Porträt von Iwan, Gründer von LEVAMI." } },
  "hist-s2": {
    title: { fr: "Revenir", en: "Coming back", de: "Zurückkehren" },
    body: {
      fr: "Pendant plus de dix ans, Iwan a travaillé dans le secteur humanitaire en Syrie, en Grèce et en France.\n\nAprès plus de onze années loin de la Syrie, il y est retourné. Il a retrouvé des proches, des familles, des ateliers et des personnes dont les savoir-faire avaient traversé les années de guerre et de crise.\n\nCette expérience a donné naissance à LEVAMI.",
      en: "For more than ten years, Iwan worked in the humanitarian sector in Syria, Greece and France.\n\nAfter more than eleven years away from Syria, he returned. He reconnected with relatives, families, workshops and people whose skills had survived years of war and crisis.\n\nThis experience gave rise to LEVAMI.",
      de: "Über zehn Jahre lang arbeitete Iwan im humanitären Bereich in Syrien, Griechenland und Frankreich.\n\nNach mehr als elf Jahren fern von Syrien kehrte er zurück. Er traf Angehörige, Familien, Werkstätten und Menschen wieder, deren Fertigkeiten die Kriegs- und Krisenjahre überstanden hatten.\n\nAus dieser Erfahrung entstand LEVAMI.",
    },
  },
  "hist-quote1": { body: { fr: "Je voulais créer une marque ancrée dans une histoire réelle, pas seulement dans une esthétique.", en: "I wanted to create a brand rooted in a real story, not just an aesthetic.", de: "Ich wollte eine Marke schaffen, die in einer echten Geschichte verwurzelt ist, nicht nur in einer Ästhetik." } },
  "hist-s3": {
    title: { fr: "Des liens de confiance", en: "Bonds of trust", de: "Vertrauensvolle Verbindungen" },
    body: {
      fr: "LEVAMI se construit avec des membres de la famille, des amis, des artisans, des couturiers et des ateliers connus depuis plusieurs années.\n\nLe frère d'Iwan et d'autres proches participent directement au projet. Une partie de ce réseau se trouve à Damas et dans sa campagne.\n\nCes liens se sont construits au fil des années, entre famille, amis et partenaires de confiance.",
      en: "LEVAMI is built with family members, friends, artisans, seamstresses and workshops known for several years.\n\nIwan's brother and other relatives are directly involved in the project. Part of this network is based in Damascus and its countryside.\n\nThese bonds were built over the years, between family, friends and trusted partners.",
      de: "LEVAMI entsteht mit Familienmitgliedern, Freunden, Kunsthandwerkern, Schneiderinnen und Werkstätten, die seit mehreren Jahren bekannt sind.\n\nIwans Bruder und weitere Angehörige sind direkt am Projekt beteiligt. Ein Teil dieses Netzwerks befindet sich in Damaskus und seinem Umland.\n\nDiese Verbindungen sind über die Jahre gewachsen, zwischen Familie, Freunden und vertrauensvollen Partnern.",
    },
  },
  "hist-reseau": { image_0_alt: { fr: "Iwan entouré de proches, réunis autour d'une carte en bois du Levant, symbole du réseau humain de LEVAMI.", en: "Iwan surrounded by relatives, gathered around a wooden map of the Levant, symbol of LEVAMI's human network.", de: "Iwan umgeben von Angehörigen, versammelt um eine Holzkarte der Levante, Symbol für das menschliche Netzwerk von LEVAMI." } },
  "hist-s4": {
    title: { fr: "Ce que fait LEVAMI aujourd'hui", en: "What LEVAMI does today", de: "Was LEVAMI heute tut" },
    body: {
      fr: "Certaines pièces sont développées sous l'identité LEVAMI. D'autres sont codéveloppées, commandées ou sélectionnées auprès d'ateliers partenaires.\n\nChaque fiche produit indique le rôle de LEVAMI, le lieu de production, les matières et les techniques utilisées lorsque ces informations sont disponibles.",
      en: "Some pieces are developed under the LEVAMI identity. Others are co-developed, commissioned or sourced from partner workshops.\n\nEach product page indicates LEVAMI's role, the place of production, and the materials and techniques used, whenever this information is available.",
      de: "Manche Stücke werden unter der Identität von LEVAMI entwickelt. Andere werden gemeinsam entwickelt, in Auftrag gegeben oder bei Partnerwerkstätten ausgewählt.\n\nJede Produktseite gibt die Rolle von LEVAMI, den Produktionsort sowie die verwendeten Materialien und Techniken an, sofern diese Informationen verfügbar sind.",
    },
  },
  "hist-s5": {
    title: { fr: "Une histoire qui continue", en: "A story that continues", de: "Eine Geschichte, die weitergeht" },
    body: {
      fr: "LEVAMI avance à son rythme, à travers des collections à taille humaine, des collaborations et des liens directs.\n\nL'objectif n'est pas de figer le passé. Il est de faire une place à ces pièces dans la vie d'aujourd'hui.",
      en: "LEVAMI moves forward at its own pace, through human-scale collections, collaborations and direct relationships.\n\nThe goal is not to freeze the past. It is to give these pieces a place in life today.",
      de: "LEVAMI geht sein eigenes Tempo, durch Kollektionen in menschlichem Maßstab, Kooperationen und direkte Verbindungen.\n\nDas Ziel ist nicht, die Vergangenheit einzufrieren. Es geht darum, diesen Stücken einen Platz im heutigen Leben zu geben.",
    },
  },
  "hist-finalquote": { body: { fr: "Ce n'est pas une nostalgie figée. C'est une histoire qui continue.", en: "This is not a frozen nostalgia. It is a story that continues.", de: "Das ist keine erstarrte Nostalgie. Es ist eine Geschichte, die weitergeht." } },

  // ---- Savoir-faire ----------------------------------------------------------
  "art-intro": {
    title: { fr: "Un geste transmis", en: "A gesture passed on", de: "Eine weitergegebene Geste" },
    body: {
      fr: "Depuis des générations, des personnes brodent, tissent, coupent et assemblent les textiles du Levant.\n\nLEVAMI travaille avec des proches, des artisans et des ateliers pour donner une place actuelle à ces gestes.",
      en: "For generations, people have embroidered, woven, cut and assembled textiles from the Levant.\n\nLEVAMI works with relatives, artisans and workshops to give these gestures a place today.",
      de: "Seit Generationen sticken, weben, schneiden und verarbeiten Menschen die Textilien der Levante.\n\nLEVAMI arbeitet mit Angehörigen, Kunsthandwerkern und Werkstätten, um diesen Gesten einen zeitgemäßen Platz zu geben.",
    },
  },
  "art-s1": {
    title: { fr: "Des personnes et des ateliers connus", en: "People and workshops we know", de: "Bekannte Menschen und Werkstätten" },
    body: {
      fr: "LEVAMI ne travaille pas avec un réseau anonyme. Le projet s'appuie sur des relations construites avec des proches, des artisans, des couturiers et des ateliers liés aux communautés palestiniennes et syriennes.",
      en: "LEVAMI does not work with an anonymous network. The project relies on relationships built with relatives, artisans, seamstresses and workshops connected to Palestinian and Syrian communities.",
      de: "LEVAMI arbeitet nicht mit einem anonymen Netzwerk. Das Projekt stützt sich auf über Jahre gewachsene Beziehungen zu Angehörigen, Kunsthandwerkern, Schneiderinnen und Werkstätten aus palästinensischen und syrischen Gemeinschaften.",
    },
  },
  "art-gallery1": {
    image_0_alt: { fr: "Détail de broderie multicolore sur robe Levami.", en: "Detail of multicoloured embroidery on a LEVAMI dress.", de: "Detail einer mehrfarbigen Stickerei auf einem LEVAMI-Kleid." },
    image_1_alt: { fr: "Détail de broderie tatreez sur pièce Levami.", en: "Detail of tatreez embroidery on a LEVAMI piece.", de: "Detail einer Tatreez-Stickerei auf einem LEVAMI-Stück." },
  },
  "art-s2": {
    title: { fr: "De Yarmouk à la campagne de Damas", en: "From Yarmouk to the countryside of Damascus", de: "Von Yarmouk in das Umland von Damaskus" },
    body: {
      fr: "Une partie des pièces LEVAMI est développée avec un atelier installé dans la campagne de Damas.\n\nCet atelier était historiquement lié à Yarmouk, l'une des principales communautés palestiniennes de Syrie avant la guerre.\n\nSon équipe rassemble notamment des travailleurs palestiniens ainsi que des artisans et artisanes syriens.",
      en: "Some LEVAMI pieces are developed with a workshop based in the countryside of Damascus.\n\nThis workshop was historically linked to Yarmouk, one of the main Palestinian communities in Syria before the war.\n\nIts team notably brings together Palestinian workers as well as Syrian craftsmen and craftswomen.",
      de: "Ein Teil der LEVAMI-Stücke wird mit einer Werkstatt im Umland von Damaskus entwickelt.\n\nDiese Werkstatt war historisch mit Yarmouk verbunden, einer der wichtigsten palästinensischen Gemeinschaften Syriens vor dem Krieg.\n\nIhr Team vereint insbesondere palästinensische Arbeiter sowie syrische Kunsthandwerkerinnen und Kunsthandwerker.",
    },
  },
  "art-s3": {
    title: { fr: "Tatreez et broderie", en: "Tatreez and embroidery", de: "Tatreez und Stickerei" },
    body: {
      fr: "Le tatreez est une technique de broderie au point de croix, réalisée à l'aiguille, motif par motif. LEVAMI l'utilise sur une sélection de pièces où la technique et la matière du produit sont précisées sur sa fiche.",
      en: "Tatreez is a cross-stitch embroidery technique, worked by hand, needle by needle, motif by motif. LEVAMI uses it on a selection of pieces, where the technique and material are specified on the product page.",
      de: "Tatreez ist eine Kreuzstich-Sticktechnik, die von Hand, Nadel für Nadel, Motiv für Motiv ausgeführt wird. LEVAMI verwendet sie bei einer Auswahl an Stücken, bei denen Technik und Material auf der Produktseite angegeben sind.",
    },
  },
  "art-gallery2": { image_0_alt: { fr: "Détail de broderie sur keffieh Levami.", en: "Detail of embroidery on a LEVAMI keffieh.", de: "Detail einer Stickerei auf einem LEVAMI-Keffieh." } },
  "art-s4": {
    title: { fr: "Confection textile", en: "Textile making", de: "Textilverarbeitung" },
    body: {
      fr: "La confection d'une pièce LEVAMI passe par plusieurs étapes : la coupe des tissus, l'assemblage, la broderie lorsqu'elle est prévue, les finitions et le contrôle qualité avant expédition.",
      en: "Making a LEVAMI piece involves several steps: cutting the fabric, assembling, embroidery when planned, finishing touches and quality control before shipping.",
      de: "Die Herstellung eines LEVAMI-Stücks umfasst mehrere Schritte: Zuschnitt der Stoffe, Zusammenfügen, Stickerei falls vorgesehen, Verarbeitung der Details und Qualitätskontrolle vor dem Versand.",
    },
  },

  // ---- Collaborations & Contact (single intro block) --------------------------
  "part-intro": {
    title: { fr: "Collaborer avec LEVAMI", en: "Collaborate with LEVAMI", de: "Mit LEVAMI zusammenarbeiten" },
    body: {
      fr: "LEVAMI travaille avec des boutiques, des associations, des festivals et des lieux culturels en France et en Europe.\n\nNous pouvons imaginer ensemble une sélection boutique, un pop-up, un dépôt-vente, une commande groupée ou un événement culturel.",
      en: "LEVAMI works with shops, associations, festivals and cultural venues in France and Europe.\n\nTogether, we can imagine a shop selection, a pop-up, a consignment deal, a group order or a cultural event.",
      de: "LEVAMI arbeitet mit Geschäften, Vereinen, Festivals und Kulturorten in Frankreich und Europa zusammen.\n\nGemeinsam können wir eine Shop-Auswahl, ein Pop-up, einen Kommissionsverkauf, eine Sammelbestellung oder eine kulturelle Veranstaltung planen.",
    },
  },
  "contact-intro": { title: "contact.title" },
};

export const LITERAL_DEFAULTS = {
  "hero-default": { image: "/corrected/img4287-hero.webp", cta1_url: "/boutique", cta2_url: "/histoire" },
  "story-founder-default": { image: "/api/files/levami/media/e5b68890-f92f-4d9f-913e-9c595db7c4b7.webp", cta_url: "/histoire" },
  "story-craft-default": { image: "/api/files/levami/media/202945af-5c0a-480f-a09d-32eaa0056369.webp", cta_url: "/artisanat" },
  "lookbook-default": { image_main: "/corrected/img3981-lookbook.webp", image_secondary: "/corrected/dsc08929-corrected.webp", cta_url: "/lookbook" },
  "hist-portrait": { image_0_url: "/corrected/img1281-fondateur.webp" },
  "hist-reseau": { image_0_url: "/api/files/levami/media/9a2c148a-aea8-470c-8e19-492e11dc0f05.webp" },
  "art-gallery1": {
    image_0_url: "/api/files/levami/media/e5b68890-f92f-4d9f-913e-9c595db7c4b7.webp",
    image_1_url: "/corrected/dsc08929-corrected.webp",
  },
  "art-gallery2": { image_0_url: "/api/files/levami/media/202945af-5c0a-480f-a09d-32eaa0056369.webp" },
};

/** Resolve a single field's display value: explicit admin value > i18n fallback (t() key or {fr,en,de}) > literal default > "" */
export function resolveField(sectionId, field, settings, t, locale = "fr") {
  const v = settings?.[field];
  if (v !== undefined && v !== null && v !== "") return v;
  const fallback = I18N_FALLBACKS[sectionId]?.[field];
  if (fallback) {
    if (typeof fallback === "string") return t(fallback);
    return fallback[locale] || fallback.fr || "";
  }
  const lit = LITERAL_DEFAULTS[sectionId]?.[field];
  if (lit !== undefined) return lit;
  return "";
}

/** Resolve a full image list for an "image" block: admin-edited array (already flat
 * strings) takes priority; otherwise builds the default array from LITERAL_DEFAULTS
 * (image_N_url) + I18N_FALLBACKS (image_N_alt). */
export function resolveImages(sectionId, settings, t, locale = "fr") {
  if (Array.isArray(settings?.images)) return settings.images;
  const images = [];
  for (let i = 0; i < 6; i++) {
    const url = LITERAL_DEFAULTS[sectionId]?.[`image_${i}_url`];
    if (!url) break;
    const altFallback = I18N_FALLBACKS[sectionId]?.[`image_${i}_alt`];
    const alt = altFallback ? (typeof altFallback === "string" ? t(altFallback) : (altFallback[locale] || altFallback.fr || "")) : "";
    images.push({ url, alt, caption: "", label: "" });
  }
  return images;
}

export const SECTION_TYPE_META = {
  hero: { label: "Hero plein écran", icon: "GalleryHorizontalEnd" },
  banner: { label: "Bannière image", icon: "Image" },
  image_text: { label: "Image + Texte", icon: "Columns2" },
  collections: { label: "Collections", icon: "LayoutGrid" },
  featured_products: { label: "Produits mis en avant", icon: "Star" },
  lookbook: { label: "Lookbook", icon: "Images" },
  stories: { label: "Stories", icon: "BookOpen" },
  workshops: { label: "Ateliers", icon: "Hammer" },
  principles: { label: "Points forts", icon: "ListChecks" },
  newsletter: { label: "Newsletter", icon: "Mail" },
  text: { label: "Bloc de texte", icon: "Type" },
  image: { label: "Image(s)", icon: "Image" },
};

export const ADDABLE_SECTION_TYPES_BY_PAGE = {
  home: ["hero", "banner", "image_text", "collections", "featured_products", "lookbook", "stories", "workshops", "principles", "newsletter"],
  histoire: ["text", "image"],
  artisanat: ["text", "image"],
  partenariats: ["text", "image"],
  contact: ["text", "image"],
};

export function defaultSettingsForType(type) {
  switch (type) {
    case "hero":
      return {
        media_mode: "image", eyebrow: "", title_line1: "", title_line2: "", subtitle: "",
        cta1_label: "", cta1_url: "/boutique", cta2_label: "", cta2_url: "",
        image: "", image_mobile: "", image_alt: "",
        slider_images: [], slider_autoplay: true, slider_duration: 5, slider_arrows: true, slider_dots: true, slider_transition: "fade",
        video_url: "", video_poster: "",
      };
    case "banner":
      return { image: "", image_mobile: "", focal_point: "center", headline: "", subtext: "", cta_label: "", cta_url: "", text_align: "center", overlay_opacity: 30, height: "large" };
    case "image_text":
      return { eyebrow: "", title: "", quote: "", body_p1: "", body_p2: "", cta_label: "", cta_url: "", image: "", image_alt: "", image_position: "left", background: "default", variant: "standard" };
    case "collections":
      return { title: "", mode: "auto", category_ids: [] };
    case "featured_products":
      return { title: "", mode: "auto", auto_source: "featured", limit: 8, product_ids: [] };
    case "lookbook":
      return { title: "", subtitle: "", mode: "custom", image_main: "", image_main_alt: "", image_secondary: "", image_secondary_alt: "", lookbook_ids: [], limit: 6, cta_label: "", cta_url: "/lookbook" };
    case "stories":
      return { title: "", mode: "auto", limit: 3, story_ids: [] };
    case "workshops":
      return { title: "", mode: "auto", limit: 3, workshop_ids: [] };
    case "principles":
      return { title: "", item1_title: "", item1_text: "", item2_title: "", item2_text: "", item3_title: "", item3_text: "" };
    case "newsletter":
      return { title: "", subtitle: "" };
    case "text":
      return { label: "", title: "", body: "", is_quote: false };
    case "image":
      return { images: [] };
    default:
      return {};
  }
}

export const FOCAL_POINT_OPTIONS = [
  { value: "top left", label: "Haut gauche" }, { value: "top center", label: "Haut centre" }, { value: "top right", label: "Haut droite" },
  { value: "center left", label: "Centre gauche" }, { value: "center", label: "Centre" }, { value: "center right", label: "Centre droite" },
  { value: "bottom left", label: "Bas gauche" }, { value: "bottom center", label: "Bas centre" }, { value: "bottom right", label: "Bas droite" },
];

/** Drives the generic settings form in the admin Website Editor. Each entry:
 * { key, label, kind, options?, min?, max? }. Special kinds (image_list,
 * category_picker, product_picker, lookbook_picker, story_picker, workshop_picker,
 * hero_media, toggle) are rendered by dedicated components in AdminTheme.js rather
 * than a plain input. */
export const FIELD_SCHEMAS = {
  hero: [
    { key: "media", label: "Média du hero (image / diaporama / vidéo)", kind: "hero_media" },
    { key: "eyebrow", label: "Texte au-dessus du titre (facultatif)", kind: "text" },
    { key: "title_line1", label: "Titre — ligne 1", kind: "text" },
    { key: "title_line2", label: "Titre — ligne 2", kind: "text" },
    { key: "subtitle", label: "Sous-titre", kind: "textarea" },
    { key: "cta1_label", label: "Bouton 1 — texte", kind: "text" },
    { key: "cta1_url", label: "Bouton 1 — lien", kind: "url" },
    { key: "cta2_label", label: "Bouton 2 — texte", kind: "text" },
    { key: "cta2_url", label: "Bouton 2 — lien", kind: "url" },
  ],
  banner: [
    { key: "video_url", label: "Vidéo (facultatif)", kind: "video" },
    { key: "image", label: "Image (bureau)", kind: "image" },
    { key: "image_mobile", label: "Image (mobile, facultatif)", kind: "image" },
    { key: "focal_point", label: "Point focal de l'image", kind: "select", options: FOCAL_POINT_OPTIONS },
    { key: "headline", label: "Titre", kind: "text" },
    { key: "subtext", label: "Texte", kind: "textarea" },
    { key: "cta_label", label: "Bouton — texte", kind: "text" },
    { key: "cta_url", label: "Bouton — lien", kind: "url" },
    { key: "text_align", label: "Alignement du texte", kind: "select", options: [{ value: "left", label: "Gauche" }, { value: "center", label: "Centre" }, { value: "right", label: "Droite" }] },
    { key: "overlay_opacity", label: "Opacité du voile sombre (%)", kind: "slider", min: 0, max: 80 },
    { key: "height", label: "Hauteur de la bannière", kind: "select", options: [{ value: "large", label: "Grande" }, { value: "medium", label: "Moyenne" }] },
  ],
  image_text: [
    { key: "video_url", label: "Vidéo (facultatif)", kind: "video" },
    { key: "image", label: "Image", kind: "image" },
    { key: "image_alt", label: "Texte alternatif de l'image", kind: "text" },
    { key: "image_position", label: "Position de l'image", kind: "select", options: [{ value: "left", label: "Gauche" }, { value: "right", label: "Droite" }] },
    { key: "background", label: "Fond de section", kind: "select", options: [{ value: "default", label: "Standard" }, { value: "ivoire", label: "Ivoire (encadré)" }] },
    { key: "eyebrow", label: "Texte au-dessus du titre (facultatif)", kind: "text" },
    { key: "title", label: "Titre", kind: "text" },
    { key: "quote", label: "Citation en italique (facultatif)", kind: "textarea" },
    { key: "body_p1", label: "Paragraphe 1", kind: "textarea" },
    { key: "body_p2", label: "Paragraphe 2", kind: "textarea" },
    { key: "cta_label", label: "Bouton — texte", kind: "text" },
    { key: "cta_url", label: "Bouton — lien", kind: "url" },
  ],
  collections: [
    { key: "title", label: "Titre de la section", kind: "text" },
    { key: "mode", label: "Source des catégories", kind: "select", options: [{ value: "auto", label: "Automatique (toutes les catégories)" }, { value: "manual", label: "Sélection manuelle" }] },
    { key: "category_ids", label: "Catégories affichées (mode manuel)", kind: "category_picker", showIf: (s) => s.mode === "manual" },
  ],
  featured_products: [
    { key: "title", label: "Titre de la section", kind: "text" },
    { key: "mode", label: "Source des produits", kind: "select", options: [{ value: "auto", label: "Automatique" }, { value: "manual", label: "Sélection manuelle" }] },
    { key: "auto_source", label: "Mode automatique", kind: "select", options: [{ value: "featured", label: "Produits mis en avant" }, { value: "newest", label: "Nouveautés" }], showIf: (s) => s.mode !== "manual" },
    { key: "limit", label: "Nombre de produits affichés", kind: "number", showIf: (s) => s.mode !== "manual" },
    { key: "product_ids", label: "Produits (mode manuel)", kind: "product_picker", showIf: (s) => s.mode === "manual" },
  ],
  lookbook: [
    { key: "title", label: "Titre", kind: "text" },
    { key: "subtitle", label: "Sous-titre", kind: "textarea" },
    { key: "mode", label: "Source des images", kind: "select", options: [{ value: "custom", label: "Images personnalisées" }, { value: "auto", label: "Dernières images publiées" }, { value: "manual", label: "Sélection manuelle" }] },
    { key: "image_main", label: "Image principale (mode personnalisé)", kind: "image", showIf: (s) => (s.mode || "custom") === "custom" },
    { key: "image_main_alt", label: "Alt — image principale", kind: "text", showIf: (s) => (s.mode || "custom") === "custom" },
    { key: "image_secondary", label: "Image secondaire (mode personnalisé)", kind: "image", showIf: (s) => (s.mode || "custom") === "custom" },
    { key: "image_secondary_alt", label: "Alt — image secondaire", kind: "text", showIf: (s) => (s.mode || "custom") === "custom" },
    { key: "limit", label: "Nombre d'images (mode automatique)", kind: "number", showIf: (s) => s.mode === "auto" },
    { key: "lookbook_ids", label: "Images Lookbook (mode manuel)", kind: "lookbook_picker", showIf: (s) => s.mode === "manual" },
    { key: "cta_label", label: "Bouton — texte", kind: "text" },
    { key: "cta_url", label: "Bouton — lien", kind: "url" },
  ],
  stories: [
    { key: "title", label: "Titre de la section", kind: "text" },
    { key: "mode", label: "Source des articles", kind: "select", options: [{ value: "auto", label: "Automatique (plus récents)" }, { value: "manual", label: "Sélection manuelle" }] },
    { key: "limit", label: "Nombre d'articles (mode automatique)", kind: "number", showIf: (s) => s.mode !== "manual" },
    { key: "story_ids", label: "Articles affichés (mode manuel)", kind: "story_picker", showIf: (s) => s.mode === "manual" },
  ],
  workshops: [
    { key: "title", label: "Titre de la section", kind: "text" },
    { key: "mode", label: "Source des ateliers", kind: "select", options: [{ value: "auto", label: "Automatique" }, { value: "manual", label: "Sélection manuelle" }] },
    { key: "limit", label: "Nombre d'ateliers (mode automatique)", kind: "number", showIf: (s) => s.mode !== "manual" },
    { key: "workshop_ids", label: "Ateliers affichés (mode manuel)", kind: "workshop_picker", showIf: (s) => s.mode === "manual" },
  ],
  principles: [
    { key: "title", label: "Titre de la section", kind: "text" },
    { key: "item1_title", label: "Point 1 — titre", kind: "text" }, { key: "item1_text", label: "Point 1 — texte", kind: "textarea" },
    { key: "item2_title", label: "Point 2 — titre", kind: "text" }, { key: "item2_text", label: "Point 2 — texte", kind: "textarea" },
    { key: "item3_title", label: "Point 3 — titre", kind: "text" }, { key: "item3_text", label: "Point 3 — texte", kind: "textarea" },
  ],
  newsletter: [
    { key: "title", label: "Titre", kind: "text" },
    { key: "subtitle", label: "Sous-titre", kind: "textarea" },
  ],
  text: [
    { key: "label", label: "Petit label (facultatif)", kind: "text" },
    { key: "title", label: "Titre (facultatif)", kind: "text" },
    { key: "body", label: "Texte (laisser une ligne vide entre les paragraphes)", kind: "textarea" },
    { key: "is_quote", label: "Afficher comme une citation", kind: "toggle" },
  ],
  image: [
    { key: "video_url", label: "Vidéo (facultatif)", kind: "video" },
    { key: "images", label: "Images (1 = pleine largeur, 2 = duo, 3+ = galerie)", kind: "image_list" },
  ],
};

/** Blocks whose default text is a large centered "intro" (h1 + lead paragraph)
 * rather than a regular in-page section (h2). Drives visual treatment only —
 * the underlying field schema (label/title/body) is identical either way. */
export const INTRO_BLOCK_IDS = ["hist-intro", "art-intro", "part-intro", "contact-intro"];

/** Known layout overrides for image blocks that predate the Website Editor, so
 * migrating them to the generic "image" block type reproduces the exact same
 * visual result (no design change for existing pages). New admin-added image
 * blocks fall back to a sensible auto layout based on image count. */
export const IMAGE_BLOCK_LAYOUT_OVERRIDES = {
  "hist-portrait": { mode: "single", maxW: "560px", ratio: 4 / 5 },
  "hist-reseau": { mode: "single", maxW: "900px", ratio: 16 / 10 },
  "art-gallery1": { mode: "duo", maxW: "900px", ratio: 1 },
  "art-gallery2": { mode: "single", maxW: "900px", ratio: 16 / 9 },
};

/** Copy what is currently displayed, including assets inherited by shipped blocks. */
export function settingsForDuplicate(section, t) {
  const settings = { ...(section.settings || {}) };
  const keys = new Set([
    ...Object.keys(I18N_FALLBACKS[section.id] || {}),
    ...Object.keys(LITERAL_DEFAULTS[section.id] || {}),
    ...(FIELD_SCHEMAS[section.type] || []).map((field) => field.key),
  ]);
  keys.forEach((key) => {
    if (key === "media" || key === "images") return;
    const value = resolveField(section.id, key, settings, t);
    if (value !== "") settings[key] = value;
  });
  if (section.type === "image") settings.images = resolveImages(section.id, settings, t);
  return settings;
}
